import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PlatformRole } from '@/lib/permissions';

export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'waitlisted';
export type AgencyRole = 'owner' | 'admin' | 'manager' | 'staff' | null;

interface AgencyContextType {
  agencyId: string | null;
  agencyRole: AgencyRole;
  isAgencyAdmin: boolean;
  /** True if user has ANY row in super_admins (any platform role). */
  isSuperAdmin: boolean;
  /** Specific platform role, or null for non-platform users. */
  platformRole: PlatformRole | null;
  /** True only if platformRole === 'platform_super_admin'. */
  isPlatformSuperAdmin: boolean;
  onboardingComplete: boolean;
  approvalStatus: ApprovalStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AgencyContext = createContext<AgencyContextType>({
  agencyId: null,
  agencyRole: null,
  isAgencyAdmin: false,
  isSuperAdmin: false,
  platformRole: null,
  isPlatformSuperAdmin: false,
  onboardingComplete: false,
  approvalStatus: null,
  loading: true,
  refresh: async () => {},
});


export function AgencyProvider({ children }: { children: ReactNode }) {
  const { user, role } = useAuth();
  const [agencyId, setAgencyId] = useState<string | null>(null);
  const [agencyRole, setAgencyRole] = useState<AgencyRole>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [platformRole, setPlatformRole] = useState<PlatformRole | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user?.id) {
      setAgencyId(null);
      setAgencyRole(null);
      setIsSuperAdmin(false);
      setPlatformRole(null);
      setOnboardingComplete(false);
      setApprovalStatus(null);
      setLoading(false);
      return;
    }
    try {
      const { data: saData } = await supabase
        .from('super_admins')
        .select('id, role')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();
      const superAdmin = !!saData;
      setIsSuperAdmin(superAdmin);
      setPlatformRole((saData as any)?.role ?? null);


      const { data: profile } = await supabase
        .from('profiles')
        .select('agency_id, agency_role, onboarding_complete, email, approval_status')
        .eq('id', user.id)
        .maybeSingle();

      let resolvedAgencyId = profile?.agency_id ?? null;
      // Approval gate is removed — every agency user is effectively approved.
      // We still surface the raw status for the Settings/Audit UIs.
      const rawStatus = ((profile as any)?.approval_status ?? 'approved') as ApprovalStatus;
      const effectiveStatus: ApprovalStatus = 'approved';

      if (!resolvedAgencyId) {
        const { data: claimed, error: claimErr } = await supabase.rpc('claim_owned_agency');
        if (claimErr) console.error('[AgencyContext] claim_owned_agency failed:', claimErr);
        else if (claimed) resolvedAgencyId = claimed as string;
      }

      setAgencyId(resolvedAgencyId);
      setAgencyRole(((profile as any)?.agency_role ?? null) as AgencyRole);
      setOnboardingComplete(!!profile?.onboarding_complete);
      setApprovalStatus(effectiveStatus);
      void rawStatus;
    } catch (err) {
      console.error('AgencyContext error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, role]);

  // Agency admin is PURELY an agency-scoped role. Platform staff do NOT
  // automatically inherit agency-admin powers — they must use the Platform
  // Console for cross-agency oversight, or use a future "View as agency"
  // impersonation flow.
  const isAgencyAdmin = agencyRole === 'owner' || agencyRole === 'admin';
  const isPlatformSuperAdmin = platformRole === 'platform_super_admin';

  return (
    <AgencyContext.Provider
      value={{ agencyId, agencyRole, isAgencyAdmin, isSuperAdmin, platformRole, isPlatformSuperAdmin, onboardingComplete, approvalStatus, loading, refresh: load }}
    >
      {children}
    </AgencyContext.Provider>
  );
}


export function useAgency() {
  return useContext(AgencyContext);
}
